import { create, v, w } from '@dojo/framework/core/vdom';
import { DNode } from '@dojo/framework/core/interfaces';
import { theme } from '@dojo/framework/core/middleware/theme';

import { createNlsMiddleware } from 'lcc-state';
import { DonutChart } from '@holmescorp/charts';

import css from '../css/score-chart.m.css';
import { getScoreStyle } from '../main';

interface ScoreChartProps {
	score: number;
	bestScore?: number;
	useInsideLabel?: boolean;
	insideLabelText?: string;
	bestScoreText?: string;
	useScoreColor?: boolean;
	useBestScoreIcon?: boolean;
	additionalClasses?: string[];
	icon?: DNode;
}

const lang = window.hcstatic.portal_language || 'en';
const pct = Intl.NumberFormat(lang, { style: 'percent', maximumSignificantDigits: 2 });

const factory = create({
	nls: createNlsMiddleware('ditabook'),
	theme
}).properties<ScoreChartProps>();

export const ScoreChart = factory(({ middleware: { nls, theme }, properties }) => {
	const {
		messages: { assess_progress_threshold_low = '60', assess_progress_threshold_high = '80' }
	} = nls();
	const {
		score,
		bestScore = '',
		useInsideLabel = true,
		additionalClasses = [],
		useScoreColor = true,
		insideLabelText = 'Test score',
		bestScoreText = 'Best Quiz Score',
		useBestScoreIcon = true,
		icon
	} = properties();

	const themeCss = theme.classes(css);

	const lowScore = parseInt(assess_progress_threshold_low) || 60;
	const highScore = parseInt(assess_progress_threshold_high) || 80;

	const bestScoreValue = bestScore || -1;
	const bestScoreColor = getScoreStyle(bestScoreValue, lowScore, highScore, themeCss);

	const chartColor =
		score < lowScore ? 'rgb(156, 4, 19)' : score < highScore ? 'rgb(255, 146, 0)' : 'rgb(0, 183, 91)';

	const labels = [];

	if (useInsideLabel) {
		labels.push({
			text: insideLabelText,
			font: {
				size: '12'
			}
		});
	}

	labels.push({
		text: pct.format(score / 100),
		font: {
			size: '20'
		}
	});

	return score >= 0
		? v('div', { classes: [themeCss.scoreChart] }, [
				bestScore
					? v('p', { classes: [themeCss.bestScore] }, [
							useBestScoreIcon && icon ? v('span', { classes: [bestScoreColor] }, [icon]) : null,
							`${bestScoreText}: ${bestScoreValue}%`
					  ])
					: null,
				v('div', { classes: [themeCss.scoreContainer, ...additionalClasses] }, [
					w(DonutChart, {
						data: {
							datasets: [
								{
									data: [score, 100 - score],
									backgroundColor: [
										useScoreColor ? chartColor : 'rgba(89, 166, 60, 1)',
										'rgba(245, 245, 245, 1)'
									]
								}
							]
						},
						options: {
							responsive: true,
							maintainAspectRatio: true,
							tooltips: {
								enabled: false
							},
							cutoutPercentage: 85,
							scales: {},
							plugins: {
								doughnutlabel: {
									labels
								}
							}
						},
						legend: {
							display: false
						}
					})
				])
		  ])
		: null;
});

export default ScoreChart;
